import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Users, Search, Plus, X, Loader2, Shield, CheckCircle,
  XCircle, Mail, Phone, Clock,
} from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { apiGet, apiPost, apiPatch } from '@/lib/api'
import { cn, formatDate, timeAgo } from '@/lib/utils'

interface UserRecord {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
  department: string | null
  skills: string[]
  certifications: string[]
  isActive: boolean
  avatarUrl: string | null
  lastLoginAt: string | null
  propertyAccess: { propertyId: string; property: { name: string; code: string } }[]
}

const ROLES = [
  { value: 'GROUP_ADMIN', label: 'Group Admin', color: 'bg-purple-100 text-purple-800' },
  { value: 'REGIONAL_DIRECTOR', label: 'Regional Director', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'PROPERTY_MANAGER', label: 'Property Manager', color: 'bg-blue-100 text-blue-800' },
  { value: 'CHIEF_ENGINEER', label: 'Chief Engineer', color: 'bg-navy-100 text-navy-800' },
  { value: 'ENGINEER', label: 'Engineer', color: 'bg-teal-100 text-teal-800' },
  { value: 'TECHNICIAN', label: 'Technician', color: 'bg-green-100 text-green-800' },
  { value: 'CONTRACTOR', label: 'Contractor', color: 'bg-amber-100 text-amber-800' },
  { value: 'FINANCE', label: 'Finance', color: 'bg-gray-100 text-gray-700' },
  { value: 'VIEWER', label: 'Viewer', color: 'bg-gray-100 text-gray-500' },
]

const DEPARTMENTS = ['Engineering', 'Housekeeping', 'F&B', 'Management', 'Spa', 'Front Office', 'Finance', 'IT']
const SKILLS = ['HVAC', 'Electrical', 'Plumbing', 'Lifts', 'Fire Systems', 'BMS', 'Painting', 'Carpentry', 'Pool Plant', 'Kitchen Equipment']
const CERTS = ['Gas Safe', 'IPAF', 'PASMA', 'C&G 18th Edition', 'CSCS', 'First Aid', 'Fire Marshall', 'IOSH', 'CompEx']

export function UsersPage() {
  const { user: currentUser } = useAuthStore()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [filterRole, setFilterRole] = useState('')
  const [showInactive, setShowInactive] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null)

  const [form, setForm] = useState({
    email: '', password: '', firstName: '', lastName: '',
    role: 'TECHNICIAN', department: '', phone: '',
    skills: [] as string[], certifications: [] as string[],
    propertyIds: [] as string[],
  })

  // ── Queries ─────────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['users', search, filterRole, showInactive],
    queryFn: () => apiGet<{ data: UserRecord[]; pagination: any }>('/users', {
      limit: 100,
      ...(search && { search }),
      ...(filterRole && { role: filterRole }),
      isActive: !showInactive,
    }),
  })

  const { data: properties } = useQuery({
    queryKey: ['properties-list'],
    queryFn: () => apiGet<any>('/properties'),
  })

  // ── Mutations ───────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost('/users', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowAddModal(false)
      resetForm()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown>) => apiPatch(`/users/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setEditingUser(null)
    },
  })

  function resetForm() {
    setForm({ email: '', password: '', firstName: '', lastName: '', role: 'TECHNICIAN', department: '', phone: '', skills: [], certifications: [], propertyIds: [] })
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    createMutation.mutate({
      ...form,
      propertyIds: form.propertyIds.length > 0 ? form.propertyIds : undefined,
    })
  }

  function toggleArrayItem(field: 'skills' | 'certifications' | 'propertyIds', value: string) {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value],
    }))
  }

  const users = data?.data ?? []
  const propList = Array.isArray(properties) ? properties : properties?.data ?? []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-800">User Management</h1>
          <p className="text-gray-500 text-sm mt-0.5">{data?.pagination?.total ?? 0} users</p>
        </div>
        <button onClick={() => { resetForm(); setShowAddModal(true) }} className="btn-primary">
          <Plus className="w-4 h-4" />
          Add User
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="input pl-9"
            />
          </div>
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="input w-auto min-w-[160px]">
            <option value="">All Roles</option>
            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <button
            onClick={() => setShowInactive(!showInactive)}
            className={cn('btn-secondary text-xs', showInactive && 'border-red-400 text-red-600 bg-red-50')}
          >
            {showInactive ? 'Showing Inactive' : 'Show Inactive'}
          </button>
        </div>
      </div>

      {/* Users table */}
      <div className="card p-0 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-8 h-8 animate-spin text-navy-800" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-4 py-3 font-medium text-gray-500">User</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Role</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Department</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Properties</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Last Login</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {users.map(u => {
                  const roleConfig = ROLES.find(r => r.value === u.role)
                  return (
                    <tr key={u.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-navy-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-semibold text-navy-800">{u.firstName[0]}{u.lastName[0]}</span>
                          </div>
                          <div>
                            <p className="font-medium text-navy-800">{u.firstName} {u.lastName}</p>
                            <p className="text-xs text-gray-400">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn('badge', roleConfig?.color)}>{roleConfig?.label ?? u.role}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{u.department ?? '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {u.propertyAccess.map(pa => (
                            <span key={pa.propertyId} className="badge bg-gray-100 text-gray-600">{pa.property.code}</span>
                          ))}
                          {u.propertyAccess.length === 0 && <span className="text-gray-300">—</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400">
                        {u.lastLoginAt ? timeAgo(u.lastLoginAt) : 'Never'}
                      </td>
                      <td className="px-4 py-3">
                        {u.isActive ? (
                          <span className="badge bg-green-100 text-green-800">Active</span>
                        ) : (
                          <span className="badge bg-red-100 text-red-800">Inactive</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-1 justify-end">
                          {u.isActive && u.id !== currentUser?.id && (
                            <button
                              onClick={() => updateMutation.mutate({ id: u.id, isActive: false })}
                              className="text-gray-400 hover:text-red-500 p-1"
                              title="Deactivate"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                          {!u.isActive && (
                            <button
                              onClick={() => updateMutation.mutate({ id: u.id, isActive: true })}
                              className="text-gray-400 hover:text-green-600 p-1"
                              title="Reactivate"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-navy-800">Add User</h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">First Name *</label>
                  <input value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} className="input" required />
                </div>
                <div>
                  <label className="label">Last Name *</label>
                  <input value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} className="input" required />
                </div>
              </div>

              <div>
                <label className="label">Email *</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="input" required />
              </div>

              <div>
                <label className="label">Password *</label>
                <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="input" required minLength={8} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Role *</label>
                  <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="input">
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Department</label>
                  <select value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} className="input">
                    <option value="">Select...</option>
                    {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="label">Phone</label>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="input" />
              </div>

              <div>
                <label className="label">Property Access</label>
                <div className="flex flex-wrap gap-1.5">
                  {propList.map((p: any) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleArrayItem('propertyIds', p.id)}
                      className={cn('badge cursor-pointer transition-colors',
                        form.propertyIds.includes(p.id) ? 'bg-navy-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      )}
                    >
                      {p.name ?? p.code}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label">Skills</label>
                <div className="flex flex-wrap gap-1.5">
                  {SKILLS.map(s => (
                    <button key={s} type="button" onClick={() => toggleArrayItem('skills', s)}
                      className={cn('badge cursor-pointer transition-colors',
                        form.skills.includes(s) ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      )}>{s}</button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label">Certifications</label>
                <div className="flex flex-wrap gap-1.5">
                  {CERTS.map(c => (
                    <button key={c} type="button" onClick={() => toggleArrayItem('certifications', c)}
                      className={cn('badge cursor-pointer transition-colors',
                        form.certifications.includes(c) ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      )}>{c}</button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={createMutation.isPending} className="btn-primary">
                  {createMutation.isPending ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : <><Plus className="w-4 h-4" /> Create User</>}
                </button>
              </div>

              {createMutation.isError && (
                <p className="text-sm text-red-600">Failed to create user. Email may already be in use.</p>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
